import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useApi } from "@/api/useApi";
import { hasNativeCamera } from "@/camera";
import { Icon } from "@/components/Icon";
import { sendProgress, useLiveReport } from "@/live";
import { prepareReceipt } from "@/photo";
import { receiptFiles, type ReceiptVia } from "@/receiptFiles";

/** Receipts read at the same time; more wait their turn. */
const AtOnce = 3;

type Upload = {
  id: number | string;
  name: string;
  status: "waiting" | "uploading" | "reading" | "done" | "failed";
  progress: number;
  error?: string;
  /** Being added on another of the person's devices. */
  from?: "phone" | "computer";
};

/** A receipt from another device, and when it was last heard of. */
type Remote = Upload & { id: string; heard: number };

/** This page, among the person's devices: it names the receipts it tells the others about. */
const page = Math.random().toString(36).slice(2);

/** A receipt from another device that's gone quiet this long is dropped: the phone gave up. */
const Quiet = 3 * 60_000;

/** A sheet is open; it takes its own pastes and drops. */
function sheetOpen(): boolean {
  return document.querySelector("dialog[open]") !== null;
}

function hasFiles(event: DragEvent): boolean {
  return event.dataTransfer?.types.includes("Files") ?? false;
}

/**
 * Lets receipts be dropped or pasted anywhere on a draft report. Each one is uploaded and read on
 * its own, a few at a time, so the next can be added while the last is still being read. Every
 * receipt that lands refreshes the report.
 *
 * The person's other devices with this report open see each receipt's progress too, and the report
 * refreshes there as one is added: snap it on the phone, watch it arrive on the computer.
 */
export function ReceiptDropZone({
  reportId,
  onAdded,
  queueRef,
}: {
  reportId: string;
  onAdded: () => void;
  /** Set to a function that queues files the same way, for the page's own buttons. */
  queueRef?: MutableRefObject<((files: File[], via: ReceiptVia) => void) | null>;
}) {
  const api = useApi();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [dragging, setDragging] = useState(false);
  const nextId = useRef(0);
  const running = useRef(0);
  const waiting = useRef<{ id: number; file: File; via: ReceiptVia }[]>([]);
  const [remote, setRemote] = useState<Remote[]>([]);

  // The report itself refreshes from the page when one is added.
  useLiveReport(reportId, {
    onProgress: (progress) => {
      const heard = Date.now();
      setRemote((list) => [
        ...list.filter((r) => r.id !== progress.key),
        {
          id: progress.key,
          name: progress.name,
          status: progress.status,
          progress: progress.progress,
          ...(progress.error ? { error: progress.error } : {}),
          from: progress.device,
          heard,
        },
      ]);
      if (progress.status === "done") {
        window.setTimeout(
          () => setRemote((list) => list.filter((r) => r.id !== progress.key)),
          4000,
        );
      }
    },
  });

  useEffect(() => {
    if (!remote.some((r) => r.status !== "done" && r.status !== "failed")) return;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setRemote((list) =>
        list.filter((r) => r.status === "failed" || r.status === "done" || now - r.heard < Quiet),
      );
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [remote]);

  // What the person's other devices were last told about each receipt here.
  const names = useRef(new Map<number, string>());
  const told = useRef(new Map<number, { status: Upload["status"]; progress: number }>());
  const tell = useRef((id: number, status: Upload["status"], progress: number, error?: string) => {
    const last = told.current.get(id);
    if (last?.status === status && progress - last.progress < 0.1) return;
    told.current.set(id, { status, progress });
    sendProgress({
      reportId: current.current.reportId,
      key: `${page}:${String(id)}`,
      name: names.current.get(id) ?? "Receipt",
      status,
      progress,
      device: hasNativeCamera() ? "phone" : "computer",
      error: error ?? null,
    });
  });

  // The listeners live as long as the page; they reach the current values through this.
  const current = useRef({ api, reportId, onAdded });
  useEffect(() => {
    current.current = { api, reportId, onAdded };
  });

  const update = (id: number, change: Partial<Upload>) =>
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...change } : u)));

  const process = useRef(async (id: number, file: File, via: ReceiptVia) => {
    const { api: client, reportId: report, onAdded: added } = current.current;
    try {
      update(id, { status: "uploading" });
      tell.current(id, "uploading", 0);
      const { photo, sections } = await prepareReceipt(file);
      const form = new FormData();
      form.set("file", photo);
      form.set("via", via);
      for (const section of sections) form.append("section", section);
      await client.uploadForm(`/api/expenses/reports/${report}/entries/receipt`, form, (done) => {
        update(
          id,
          done < 1 ? { status: "uploading", progress: done } : { status: "reading", progress: 1 },
        );
        tell.current(id, done < 1 ? "uploading" : "reading", done);
      });
      update(id, { status: "done" });
      tell.current(id, "done", 1);
      added();
      window.setTimeout(() => setUploads((list) => list.filter((u) => u.id !== id)), 4000);
    } catch (caught) {
      const error = caught instanceof Error ? caught.message : String(caught);
      update(id, { status: "failed", error });
      tell.current(id, "failed", 0, error);
    }
  });

  const pump = useRef(() => {
    while (running.current < AtOnce && waiting.current.length > 0) {
      const next = waiting.current.shift()!;
      running.current += 1;
      void process.current(next.id, next.file, next.via).finally(() => {
        running.current -= 1;
        pump.current();
      });
    }
  });

  const add = useRef((files: File[], via: ReceiptVia) => {
    const queued = files.map((file) => ({ id: (nextId.current += 1), file, via }));
    setUploads((list) => [
      ...list,
      ...queued.map(({ id, file }) => ({
        id,
        name: file.name,
        status: "waiting" as const,
        progress: 0,
      })),
    ]);
    waiting.current.push(...queued);
    for (const { id, file } of queued) {
      names.current.set(id, file.name);
      tell.current(id, "waiting", 0);
    }
    pump.current();
  });

  useEffect(() => {
    if (!queueRef) return;
    queueRef.current = (files, via) => add.current(files, via);
    return () => {
      queueRef.current = null;
    };
  }, [queueRef]);

  useEffect(() => {
    const onDragOver = (event: DragEvent) => {
      if (!hasFiles(event) || sheetOpen()) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setDragging(true);
    };
    const onDragLeave = (event: DragEvent) => {
      // Null when the pointer leaves the window rather than moving between elements.
      if (event.relatedTarget === null) setDragging(false);
    };
    const onDrop = (event: DragEvent) => {
      if (!hasFiles(event) || sheetOpen()) return;
      event.preventDefault();
      setDragging(false);
      const files = receiptFiles(event.dataTransfer?.files);
      if (files.length > 0) add.current(files, "Drop");
      else {
        const id = (nextId.current += 1);
        setUploads((list) => [
          ...list,
          {
            id,
            name: "That file",
            status: "failed",
            progress: 0,
            error: "Drop a photo, a PDF or an email of a receipt.",
          },
        ]);
      }
    };
    const onPaste = (event: ClipboardEvent) => {
      if (sheetOpen()) return;
      const files = receiptFiles(event.clipboardData?.files);
      if (files.length === 0) return;
      event.preventDefault();
      add.current(files, "Paste");
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    document.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
      document.removeEventListener("paste", onPaste);
    };
  }, []);

  const shown: Upload[] = [...remote, ...uploads];

  return (
    <>
      {dragging && (
        <div className="drop-page" aria-hidden="true">
          <span>
            <Icon name="receipt" size={28} />
            Drop receipts to add them
          </span>
        </div>
      )}
      {shown.length > 0 && (
        <ul className="card upload-list" aria-live="polite">
          {shown.map((upload) => (
            <li key={upload.id} className={upload.status}>
              <span className="entry-icon">
                <Icon
                  name={
                    upload.status === "failed"
                      ? "alert"
                      : upload.status === "done"
                        ? "check"
                        : "receipt"
                  }
                />
              </span>
              <span className="upload-text">
                <strong>{upload.name}</strong>
                {upload.from && (
                  <span className="upload-from">
                    <Icon name={upload.from === "phone" ? "phone" : "monitor"} size={13} />
                    {upload.from === "phone" ? "From your phone" : "From another window"}
                  </span>
                )}
                <span className="muted small">
                  {upload.status === "waiting"
                    ? "Waiting…"
                    : upload.status === "uploading"
                      ? `Uploading ${String(Math.round(upload.progress * 100))}%`
                      : upload.status === "reading"
                        ? "Reading the receipt…"
                        : upload.status === "done"
                          ? "Added"
                          : upload.error}
                </span>
                {(upload.status === "uploading" || upload.status === "reading") && (
                  <progress
                    value={upload.status === "reading" ? undefined : upload.progress}
                    max={1}
                  />
                )}
              </span>
              {upload.status === "failed" && (
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Dismiss ${upload.name}`}
                  onClick={() => {
                    setUploads((list) => list.filter((u) => u.id !== upload.id));
                    setRemote((list) => list.filter((r) => r.id !== upload.id));
                  }}
                >
                  <Icon name="x" size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
